import { useCallback, useRef, useState } from 'react';
import { ApiError } from '../../../lib/api';
import { streamAsk, type StreamAskArgs } from '../../../lib/askStream';
import type { AgentProgressEvent, AskHistoryTurn, AskResponse } from '../types';
import { INSIGHTS_AGENT_NAME } from '../lib/agentBranding';

export type AgentActivityStep = {
  stepId: string;
  tool: string;
  label: string;
  detail?: string;
  state: 'active' | 'done';
};

export type AgentActivityState = {
  statusMessage: string | null;
  narration: string | null;
  composing: boolean;
  steps: AgentActivityStep[];
  draft: string;
};

const INITIAL_ACTIVITY: AgentActivityState = {
  statusMessage: null,
  narration: null,
  composing: false,
  steps: [],
  draft: '',
};

export function applyProgressEvent(
  prev: AgentActivityState,
  event: AgentProgressEvent,
): AgentActivityState {
  switch (event.type) {
    case 'status':
      return { ...prev, statusMessage: event.message };
    case 'thinking':
      return { ...prev, narration: event.text };
    case 'tool_start': {
      const steps = prev.steps.map(s =>
        s.state === 'active' ? { ...s, state: 'done' as const } : s,
      );
      const existingIdx = steps.findIndex(s => s.stepId === event.stepId);
      const nextStep: AgentActivityStep = {
        stepId: event.stepId,
        tool: event.tool,
        label: event.label,
        detail: event.detail,
        state: 'active',
      };
      if (existingIdx >= 0) {
        steps[existingIdx] = nextStep;
      } else {
        steps.push(nextStep);
      }
      return {
        ...prev,
        statusMessage: null,
        composing: false,
        draft: '',
        steps,
      };
    }
    case 'tool_done':
      return {
        ...prev,
        steps: prev.steps.map(s =>
          s.stepId === event.stepId ? { ...s, state: 'done' } : s,
        ),
      };
    case 'composing':
      return {
        ...prev,
        composing: true,
        steps: prev.steps.map(s => ({ ...s, state: 'done' as const })),
      };
    case 'delta':
      return {
        ...prev,
        composing: false,
        draft: `${prev.draft}${event.text}`,
      };
    default:
      return prev;
  }
}

/** Idle auto-send from Ask / highlight chips. Null means skip this turn. */
export function shouldDispatchPendingQuestion(
  pendingQuestion: string | null | undefined,
  opts: { busy: boolean; inFlight: boolean },
): string | null {
  const question = pendingQuestion?.trim() ?? '';
  if (!question || opts.busy || opts.inFlight) {
    return null;
  }
  return question;
}

export function useAgentAsk() {
  const [busy, setBusy] = useState(false);
  const [activity, setActivity] = useState<AgentActivityState>(INITIAL_ACTIVITY);
  const abortRef = useRef<AbortController | null>(null);
  const inFlightRef = useRef(false);

  const resetActivity = useCallback(() => {
    setActivity(INITIAL_ACTIVITY);
  }, []);

  const isInFlight = useCallback(() => inFlightRef.current, []);

  const ask = useCallback(
    async (
      args: StreamAskArgs,
      opts?: { onProgress?: (event: AgentProgressEvent) => void },
    ): Promise<AskResponse> => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      inFlightRef.current = true;

      setBusy(true);
      setActivity(INITIAL_ACTIVITY);

      try {
        return await streamAsk(args, {
          signal: controller.signal,
          onEvent: event => {
            opts?.onProgress?.(event);
            if (
              event.type === 'done' ||
              event.type === 'error' ||
              event.type === 'mode' ||
              event.type === 'navigate'
            ) {
              return;
            }
            setActivity(prev => applyProgressEvent(prev, event));
          },
        });
      } finally {
        // A superseded request must not hide the replacement's progress.
        if (abortRef.current === controller) {
          abortRef.current = null;
          inFlightRef.current = false;
          setBusy(false);
          setActivity(prev =>
            prev.draft
              ? { ...INITIAL_ACTIVITY, draft: prev.draft }
              : INITIAL_ACTIVITY,
          );
        }
      }
    },
    [],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { ask, busy, activity, cancel, resetActivity, isInFlight };
}

export function buildAskHistory(
  messages: Array<{
    role: 'user' | 'assistant';
    text: string;
    error?: boolean;
    receipts?: AskHistoryTurn['receipts'];
  }>,
  limit = 8,
): AskHistoryTurn[] {
  return messages
    .filter(m => !m.error)
    .slice(-limit)
    .map(m => ({
      role: m.role,
      content: m.text,
      ...(m.receipts?.length ? { receipts: m.receipts } : {}),
    }));
}

/** True when the in-flight ask was cancelled (navigation, HMR, new question, etc.). */
export function isAgentAbortError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') {
    return true;
  }
  if (err instanceof ApiError && err.message === 'Request aborted.') {
    return true;
  }
  if (err instanceof Error && /aborted/i.test(err.message)) {
    return true;
  }
  return false;
}

export function agentUnavailableMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const unavailable =
      err.code === 'method-not-allowed' ||
      /not allowed|unknown method|not configured|not available/i.test(err.message);
    if (unavailable) {
      return `${INSIGHTS_AGENT_NAME} is not configured. Set LLM_API_KEY (and optionally BEDROCK_MODEL_ID / BEDROCK_PROJECT_ID) in the backend env, then restart the server.`;
    }
    return `Sorry, I hit an error: ${err.message}`;
  }
  if (err instanceof Error) {
    return `Sorry, I hit an error: ${err.message}`;
  }
  return 'Sorry, something went wrong.';
}
